import {BelegEntitiesState} from '../states/beleg-entities.state';
import {belegEntitiesAdapter} from '../adapters/beleg-entities.adapter';
import {createReducer, on} from '@ngrx/store';
import {BelegEntitiesActions} from '../actions/beleg-entities.actions';
import {PdfViewerSlot} from 'src/app/enums/pdfviewerslot.enum';
import {PdfViewerData} from '../../interfaces/pdfviewerdata.interface';

/**
 * Gibt die PDF-Viewer-Daten für einen bestimmten Slot zurück.
 */
function getSlotData(state: BelegEntitiesState, slot: PdfViewerSlot): PdfViewerData | undefined {
  return state.pdfViewerData[slot];
}

export const initialBelegEntitiesState: BelegEntitiesState = belegEntitiesAdapter.getInitialState({
  isLoading: true,
  isMoving: false,
  countedBelege: 0,
  badgeValues: [],
  reverseBelegData: {
    belegReversing: false,
  },
  vorgangsartDtos: [],
  pdfViewerData: {
    [PdfViewerSlot.Edit]: undefined,
    [PdfViewerSlot.Dialog]: undefined,
    [PdfViewerSlot.Splice]: undefined,
  },
});

export const belegEntitiesReducer = createReducer(
  initialBelegEntitiesState,

  on(BelegEntitiesActions.startReadingBelege, state => ({
    ...state,
    isLoading: true,
  })),

  on(BelegEntitiesActions.getBelegSuccess, (state, {belegDto}) => {
    const belegDtos = belegEntitiesAdapter.getSelectors().selectAll(state);
    if (belegDtos.find(currentBelegDto => currentBelegDto.id === belegDto.id)) {
      return belegEntitiesAdapter.updateOne(
        {
          id: belegDto.id,
          changes: belegDto,
        },
        state,
      );
    } else {
      return belegEntitiesAdapter.upsertOne(belegDto, {
        ...state,
        countedBelege: state.countedBelege + 1,
      });
    }
  }),

  /*
   * INFO: Reducer für readBelegeSuccess-Aktion:
   * `upsertMany` wird anstelle von `setMany` genutzt, um mehrere Belege hinzuzufügen.
   * `upsertMany` erweitert die Liste der bestehenden Belege, anstatt sie zu ersetzen.
   * Da wir die Liste mit Belegen stets erweitern, stellen wir sicher, dass keine Daten verloren gehen.
   * Die tatsächlich darzustellenden Belege werden durch Mapping in der Component geregelt.
   */
  on(BelegEntitiesActions.readBelegeSuccess, (state, {belegDtos}) => {
    return belegEntitiesAdapter.upsertMany(belegDtos, {
      ...state,
      isLoading: false,
    });
  }),

  on(BelegEntitiesActions.readBelegeFailure, state => ({
    ...state,
    isLoading: false,
  })),

  on(BelegEntitiesActions.countBelegeSuccess, (state, {countedBelege}) => ({
    ...state,
    countedBelege,
  })),

  // TODO: Dies muss unbedingt in einen InhaberEntityState, sobald das Laden der Inhaber durch den Store passiert.
  on(BelegEntitiesActions.countBadgeValueSuccess, (state, {badgeValue}) => {
    let badgeValues = [...state.badgeValues];

    const index = badgeValues.findIndex(value => {
      return value.inhaberId === badgeValue.inhaberId;
    });

    if (index === -1) {
      // INFO: Wenn die Liste komplett leer ist, muss diese initialisiert werden.
      badgeValues = [...badgeValues, badgeValue];
    } else {
      /*
       * INFO: Ist ein BadgeValue vorhanden, muss dieses entfernt
       * und das aktualisierte hinzugefügt werden.
       * Die Reihenfolge ist hierbei egal.
       */
      badgeValues.splice(index, 1);
      badgeValues.push(badgeValue);
    }

    return {
      ...state,
      badgeValues,
    };
  }),

  on(BelegEntitiesActions.entheftenSuccess, (state, action) => {
    return belegEntitiesAdapter.removeOne(action.belegIdToRemove, {
      ...state,
      countedBelege: state.countedBelege - 1,
    });
  }),

  on(BelegEntitiesActions.deleteBelegSuccess, (state, {inhaberId, belegId}) => {
    return belegEntitiesAdapter.removeOne(belegId, {
      ...state,
      lastDeletedBelegData: {
        inhaberId,
        belegId,
      },
      countedBelege: state.countedBelege - 1,
    });
  }),

  on(BelegEntitiesActions.move, (state, action) => ({
    ...state,
    isMoving: true,
  })),

  on(BelegEntitiesActions.moveSuccess, (state, action) => {
    return belegEntitiesAdapter.removeOne(action.belegId, {
      ...state,
      countedBelege: state.countedBelege - 1,
      isMoving: false,
    });
  }),

  on(BelegEntitiesActions.moveFailure, (state, action) => ({
    ...state,
    isMoving: false,
  })),

  /**
   * Sollte bei dem Laden eines neuen Belegs genau eine Dublette zu dem Beleg geben,
   * existiert bereits ein älterer Beleg (das Original).
   * Damit dieser in der Tabelle auch als Dublette gekennzeichnet wird, sofern nicht der Fall,
   * muss der Originale Beleg ebenfalls neu geladen werden.
   */
  on(BelegEntitiesActions.markOriginalBelegAsDublette, (state, {originalBelegId, dubletteId}) => {
    let belegDto = belegEntitiesAdapter
      .getSelectors()
      .selectAll(state)
      .find(belegDto => {
        return belegDto.id === originalBelegId;
      });

    if (belegDto && !belegDto.dubletten) {
      belegDto = {
        ...belegDto,
        dubletten: [dubletteId],
      };

      return belegEntitiesAdapter.updateOne(
        {
          id: belegDto.id,
          changes: belegDto,
        },
        state,
      );
    }

    return {
      ...state,
    };
  }),

  /**
   * Wird die letzte Dublette gelöscht, so soll der verbleibende Beleg (das Original)
   * nicht mehr als Dublette gekennzeichnet sein.
   */
  on(BelegEntitiesActions.unmarkOriginalBelegAsDublette, (state, {belegId}) => {
    let belegDto = belegEntitiesAdapter
      .getSelectors()
      .selectAll(state)
      .find(belegDto => {
        return belegDto.id === belegId;
      });

    if (belegDto) {
      belegDto = {
        ...belegDto,
        dubletten: undefined,
      };

      return belegEntitiesAdapter.updateOne(
        {
          id: belegDto.id,
          changes: belegDto,
        },
        state,
      );
    }

    return {
      ...state,
    };
  }),

  /**
   * Im Store vermerken, dass das Stornieren eines Belegs im Gange ist.
   */
  on(BelegEntitiesActions.reverseBeleg, state => ({
    ...state,
    reverseBelegData: {
      belegReversing: true,
      korrekturBelegId: initialBelegEntitiesState.reverseBelegData.korrekturBelegId,
      reverseBelegSuccess: initialBelegEntitiesState.reverseBelegData.reverseBelegSuccess,
    },
  })),

  /**
   * Bei erfolgreichen Stornieren den Store, sowie den alten Beleg aktualisieren.
   */
  on(BelegEntitiesActions.reverseBelegSuccess, (state, action) => {
    // INFO: Den alten Beleg vom Status auf storniert setzen
    let belegDto = belegEntitiesAdapter
      .getSelectors()
      .selectAll(state)
      .find(belegDto => {
        return belegDto.id === action.oldBelegId;
      });

    if (belegDto) {
      belegDto = {
        ...belegDto,
        offen: false,
        bearbeitet: false,
        festgeschrieben: false,
        storniert: true,
      };

      return belegEntitiesAdapter.updateOne(
        {
          id: belegDto.id,
          changes: belegDto,
        },
        {
          ...state,
          reverseBelegData: {
            ...state.reverseBelegData,
            belegReversing: false,
            reverseBelegSuccess: true,
            korrekturBelegId: action.korrekturBelegId,
          },
        },
      );
    }

    return {
      ...state,
    };
  }),

  /**
   * Bei fehlgeschlagenem Stornieren den Store aktualisieren.
   */
  on(BelegEntitiesActions.reverseBelegFailure, state => ({
    ...state,
    reverseBelegData: {
      ...state.reverseBelegData,
      belegReversing: false,
      reverseBelegSuccess: false,
    },
  })),

  on(BelegEntitiesActions.readVorgangsartenSuccess, (state, action) => ({
    ...state,
    vorgangsartDtos: [...state.vorgangsartDtos, ...action.vorgangsartDtos],
  })),

  on(BelegEntitiesActions.downloadBelegPreview, (state, {viewerSlot}) => {
    const oldData = getSlotData(state, viewerSlot);

    // INFO: Ladeindikator für den Slot setzen. Sichtbar ist während des Ladens ausschließlich der
    // Spinner (isLoading hat im jf-pdf-viewer Vorrang vor isLoaded). Die alten Vorschau-Daten bleiben
    // trotzdem erhalten: Die objectUrl wird erst im Success-/Failure-Handler freigegeben (kein
    // vorzeitiges Revoke) und der Download-Button braucht das belegDto weiterhin.
    return {
      ...state,
      pdfViewerData: {
        ...state.pdfViewerData,
        [viewerSlot]: {
          ...oldData,
          isLoading: true,
        },
      },
    };
  }),

  on(BelegEntitiesActions.downloadBelegPreviewSuccess, (state, action) => {
    const oldData = getSlotData(state, action.viewerSlot);

    // INFO: Sofern eine Vorschau-URL bereits existiert, diese freigeben.
    if (oldData?.objectUrl) {
      URL.revokeObjectURL(oldData.objectUrl);
    }

    return {
      ...state,
      pdfViewerData: {
        ...state.pdfViewerData,
        [action.viewerSlot]: {
          objectUrl: action.objectUrl,
          belegDto: action.belegDto,
        },
      },
    };
  }),

  on(BelegEntitiesActions.downloadBelegPreviewFailure, (state, {viewerSlot}) => {
    const oldData = getSlotData(state, viewerSlot);

    // INFO: Sofern eine Vorschau-URL bereits existiert, diese freigeben.
    if (oldData?.objectUrl) {
      URL.revokeObjectURL(oldData.objectUrl);
    }

    /*
     * INFO: Slot leeren statt nur den Ladeindikator zurückzusetzen. Bei einem fehlgeschlagenen
     * Belegwechsel würde sonst das alte (falsche) PDF wieder angezeigt.
     */
    return {
      ...state,
      pdfViewerData: {
        ...state.pdfViewerData,
        [viewerSlot]: undefined,
      },
    };
  }),

  on(BelegEntitiesActions.highlightText, (state, {highlightText, viewerSlot}) => {
    const oldData = getSlotData(state, viewerSlot);

    return {
      ...state,
      pdfViewerData: {
        ...state.pdfViewerData,
        [viewerSlot]: {
          ...oldData,
          highlightText,
        },
      },
    };
  }),

  on(BelegEntitiesActions.closeBelegPreviewDialog, (state, {resetPdfViewerData, viewerSlot}) => {
    // INFO: PDF-Viewer-Daten nur zurücksetzen wenn explizit angefordert
    if (!resetPdfViewerData || !viewerSlot) {
      return state;
    }

    const oldData = getSlotData(state, viewerSlot);

    // INFO: Sofern eine Vorschau-URL bereits existiert, diese freigeben.
    if (oldData?.objectUrl) {
      URL.revokeObjectURL(oldData.objectUrl);
    }

    return {
      ...state,
      pdfViewerData: {
        ...state.pdfViewerData,
        [viewerSlot]: undefined,
      },
    };
  }),
);
